import staffApi from '@/api/staff';
import { UserProps } from '@/interfaces/models/user';
import User from '@/models/user';
import { RootState } from '@/store';
import stringHelper from '@/utils/stringHelper';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';

const namespaced: boolean = true;

const getDefaultState = (): StaffState => ({
  staffs: [],
  isFetchingStaffs: false,
  isStaffsWasFetch: false,
});

export interface StaffState {
  staffs: User[];
  isFetchingStaffs: boolean;
  isStaffsWasFetch: boolean;
}

const state: StaffState = getDefaultState();

const getters: GetterTree<StaffState, RootState> = {
  adminsSortedByFullName(state) {
    return state.staffs
      .filter(({ isRoleAdmin, firstName }: User) => isRoleAdmin && firstName !== null)
      .sort((firstStaff: User, secondStaff: User) => {
        return stringHelper.sortStrings(firstStaff.fullName.toLocaleLowerCase(), secondStaff.fullName.toLocaleLowerCase());
      });
  },
  ownersSortedByFullName(state) {
    return state.staffs
      .filter(({ isOwner, firstName }: User) => isOwner && firstName !== null)
      .sort((firstStaff: User, secondStaff: User) => {
        return stringHelper.sortStrings(firstStaff.fullName.toLocaleLowerCase(), secondStaff.fullName.toLocaleLowerCase());
      });
  },
  staffsSortedByFullName(state) {
    return state.staffs
      .filter(({ isStaff, firstName }: User) => isStaff && firstName !== null)
      .sort((firstStaff: User, secondStaff: User) => {
        return stringHelper.sortStrings(firstStaff.fullName.toLocaleLowerCase(), secondStaff.fullName.toLocaleLowerCase());
      });
  },
  unconfirmedStaffsSortedByFullName(state) {
    return state.staffs
      .filter(({ firstName }: User) => firstName === null)
      .sort((firstStaff: User, secondStaff: User) => {
        return stringHelper.sortStrings(firstStaff.fullName.toLocaleLowerCase(), secondStaff.fullName.toLocaleLowerCase());
      });
  },
  getStaffById(state) {
    return (staffId: string): User | undefined => state.staffs.find(({ id }) => staffId === id);
  },
};

const actions: ActionTree<StaffState, RootState> = {
  async fetchStaffs({ commit, state }: ActionContext<StaffState, RootState>) {
    if (!state.isStaffsWasFetch && !state.isFetchingStaffs) {
      commit('setIsFetchingStaffs', true);

      return staffApi.fetchStaffs()
        .then((users: any) => {
          commit('setStaffs', users);
          commit('setIsFetchingStaffs', false);
          commit('setIsStaffsWasFetch', true);
        });
    }
  },
  async sendInvite({ dispatch }: ActionContext<StaffState, RootState>, payload: { email: string, isAdmin: boolean }) {
    const staff = await staffApi.sendInvite(payload);

    dispatch('socketCreateStaff', staff, { root: true });
  },
  async cancelInvite({ dispatch }: ActionContext<StaffState, RootState>, id: string) {
    staffApi.cancelInvite(id);

    return dispatch('socketRemoveStaff', { id }, { root: true });
  },
  async editStaff({ commit, dispatch }: ActionContext<StaffState, RootState>, editedFields: any) {
    const staff = await staffApi.editStaff(editedFields);

    return dispatch(
      'socketEditStaff',
      staff,
      { root: true },
    );
  },
  async updateAvailableSections({ commit }: ActionContext<StaffState, RootState>, { staffId, updatedFields }: any) {
    await staffApi.updateAvailableSections(staffId, updatedFields);

    commit(
      'updateAvailableSections',
      {
        staffId,
        updatedFields,
      },
    );

    commit(
      'user/updateAvailableSections',
      {
        staffId,
        updatedFields,
      },
      { root: true },
    );
  },
  async removeStaff({ dispatch }: ActionContext<StaffState, RootState>, staffId: string) {
    dispatch(
      'socketRemoveStaff',
      { id: staffId },
      { root: true },
    );

    return staffApi.removeStaff(staffId);
  },
};

const mutations: MutationTree<StaffState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setStaffs(state, staffs: User[]) {
    state.staffs = staffs;
  },
  addStaff(state, staff: User) {
    state.staffs.push(staff);
  },
  editStaff(state, staffProps: UserProps) {
    const user = state.staffs.find((staff: User) => staff.id === staffProps.id);

    if (undefined !== user) {
      user.mergeWithNewFields(staffProps);
    }
  },
  makeStaffWithPinCode(state, { id: staffId }: any) {
    const staff = state.staffs.find(({ id }) => staffId === id);

    if (staff !== undefined) {
      staff.isHavePinCode = true;
    }
  },
  updateAvailableSections(state, { staffId, updatedFields }: any) {
    const staff = state.staffs.find(({ id }) => staffId === id);

    if (staff !== undefined) {
      staff.availableSections = { ...updatedFields.availableSections };
      staff.isAdmin = updatedFields.isAdmin;
    }
  },
  removeStaff(state, staffId: string) {
    const staffIndex = state.staffs.findIndex(({ id }) => staffId === id);

    if (-1 !== staffIndex) {
      state.staffs.splice(staffIndex, 1);
    }
  },
  setIsFetchingStaffs(state, isFetchingStaffs) {
    state.isFetchingStaffs = isFetchingStaffs;
  },
  setIsStaffsWasFetch(state, isStaffsWasFetch) {
    state.isStaffsWasFetch = isStaffsWasFetch;
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
